import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Users } from "lucide-react";
import { adminClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { POSITIONS, positionLabel } from "@/lib/dvStats";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { number: "", name: "", position: POSITIONS[0] };

export default function PlayersTab() {
  const { t, lang } = useT();
  const [players, setPlayers] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await adminClient.get("/datavolley/players");
    setPlayers(data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Failed to load players"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, number: Number(form.number) };
      if (editing) {
        await adminClient.patch(`/datavolley/players/${editing.id}`, payload);
      } else {
        await adminClient.post("/datavolley/players", payload);
      }
      setForm(EMPTY);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    await adminClient.delete(`/datavolley/players/${p.id}`);
    await load();
  };

  const formEl = (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-3" data-testid="dv-player-form">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_number")}</Label>
        <Input
          type="number"
          min={0}
          max={99}
          required
          inputMode="numeric"
          value={form.number}
          onChange={(e) => setForm({ ...form, number: e.target.value })}
          data-testid="dv-player-number-input"
          className="mt-1.5 h-12 rounded-xl text-lg font-bold"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_name")}</Label>
        <Input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="dv-player-name-input"
          className="mt-1.5 h-12 rounded-xl text-lg"
        />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("dv_position")}</Label>
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="dv-position-picker">
          {POSITIONS.map((pos) => (
            <button
              key={pos}
              type="button"
              onClick={() => setForm({ ...form, position: pos })}
              data-testid={`dv-position-${pos}`}
              className={`dv-btn h-11 px-4 rounded-xl font-bold text-sm border-2 ${
                form.position === pos
                  ? "bg-ocean border-ocean text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-ocean/50"
              }`}
            >
              {positionLabel(pos, lang)}
            </button>
          ))}
        </div>
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="dv-player-submit"
        className="sm:col-span-2 dv-btn h-12 rounded-xl bg-ocean text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Plus className="h-5 w-5" /> {editing ? t("dv_save") : t("dv_add_player")}
      </button>
    </form>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4" data-testid="dv-players-tab">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-soft p-5">
        <h2 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-ocean" /> {t("dv_add_player")}
        </h2>
        {!editing && formEl}
        {editing && <p className="text-sm text-slate-500">{t("dv_edit")}: #{editing.number} {editing.name}</p>}
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-soft overflow-hidden">
        {players === null ? (
          <p className="p-8 text-center text-slate-400">…</p>
        ) : players.length === 0 ? (
          <p className="p-8 text-center text-slate-500" data-testid="dv-no-players">{t("dv_no_players")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200 text-left">
                <th className="px-4 py-3 w-16">#</th>
                <th className="px-3 py-3">{t("dv_name")}</th>
                <th className="px-3 py-3">{t("dv_position")}</th>
                <th className="px-3 py-3 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.id} className="border-b border-slate-100" data-testid={`dv-player-row-${p.number}`}>
                  <td className="px-4 py-2 font-heading font-black text-xl text-slate-900">{p.number}</td>
                  <td className="px-3 py-2 font-bold text-slate-900">{p.name}</td>
                  <td className="px-3 py-2 text-slate-600">{positionLabel(p.position, lang)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(p);
                          setForm({ number: String(p.number), name: p.name, position: p.position });
                        }}
                        data-testid={`dv-player-edit-${p.number}`}
                        className="dv-btn h-10 w-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(p)}
                        data-testid={`dv-player-delete-${p.number}`}
                        className="dv-btn h-10 w-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setForm(EMPTY);
          }
        }}
      >
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("dv_edit")}</DialogTitle>
          </DialogHeader>
          {editing && formEl}
        </DialogContent>
      </Dialog>
    </div>
  );
}
